import type { ErrorEvent, EventHint } from "@sentry/react";

let sentryModule: typeof import("@sentry/react") | null = null;

type ErrorContext = Record<string, Record<string, unknown>>;

const isAbortError = (exception: unknown) =>
  typeof exception === "object" &&
  exception !== null &&
  "name" in exception &&
  (exception as { name?: unknown }).name === "AbortError";

export const dropUnactionableErrors = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => (isAbortError(hint.originalException) ? null : event);

const getSentry = async () => {
  if (!sentryModule) {
    sentryModule = await import("@sentry/react");
  }
  return sentryModule;
};

export const logError = (exception: unknown, context?: ErrorContext) => {
  console.error(exception);

  getSentry()
    .then((Sentry) => {
      if (context) {
        Sentry.withScope((scope) => {
          Object.keys(context).forEach((key) => {
            scope.setContext(key, context[key]);
          });
          Sentry.captureException(exception);
        });
      } else {
        Sentry.captureException(exception);
      }
    })
    .catch((sentryError) => {
      console.warn("Failed to report error to Sentry:", sentryError);
    });

  return exception;
};

export const logMessage = (message: unknown, context?: ErrorContext) => {
  console.info(message);

  getSentry()
    .then((Sentry) => {
      Sentry.addBreadcrumb({
        category: "app",
        level: "info",
        message: String(message),
        data: context,
      });
    })
    .catch((sentryError) => {
      console.warn("Failed to record message in Sentry:", sentryError);
    });

  return message;
};
